import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Clock, Sun, Sunset, Moon, Star } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JadwalPage = () => {
  const { settings } = useSettings();
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [hijriDate, setHijriDate] = useState('');

  useEffect(() => {
    fetchPrayerTimes();
  }, [settings.location_city]);

  const fetchPrayerTimes = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/prayer-times`, {
        params: {
          city: settings.location_city || 'Batam',
          country: 'Indonesia'
        }
      });
      setPrayerTimes(data.data.timings);
      setHijriDate(`${data.data.date.hijri.day} ${data.data.date.hijri.month.en} ${data.data.date.hijri.year}`);
    } catch (error) {
      console.error('Error fetching prayer times:', error);
      // Fallback prayer times untuk Jakarta
      setPrayerTimes({
        Fajr: '04:45',
        Dhuhr: '12:00',
        Asr: '15:15',
        Maghrib: '18:05',
        Isha: '19:15'
      });
      setHijriDate('22 Dhul Hijjah 1446');
    }
  };

  const prayers = prayerTimes ? [
    { name: 'Subuh', time: prayerTimes.Fajr, icon: Star, desc: 'Fajar menyingsing' },
    { name: 'Dzuhur', time: prayerTimes.Dhuhr, icon: Sun, desc: 'Matahari bergeser' },
    { name: 'Ashar', time: prayerTimes.Asr, icon: Sunset, desc: 'Sore menjelang' },
    { name: 'Maghrib', time: prayerTimes.Maghrib, icon: Moon, desc: 'Matahari terbenam' },
    { name: 'Isya', time: prayerTimes.Isha, icon: Star, desc: 'Malam tiba' }
  ] : [];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-[#F8F9FA] px-6 py-8 pb-24"
    >
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold tracking-tight text-primary-dark mb-2">
          Jadwal Sholat
        </h1>
        <p className="text-sm font-body text-text-secondary">
          {settings.location_city || 'Kepulauan Riau'} • {hijriDate}
        </p>
        <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100" data-testid="kemenag-source-label">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          <span className="text-[11px] font-body font-medium text-emerald-700">Berdasarkan sumber Kemenag RI</span>
        </div>
      </div>

      <div className="space-y-4">
        {prayers.map((prayer, index) => {
          const Icon = prayer.icon;
          return (
            <motion.div
              key={prayer.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * index }}
              className="bg-white rounded-3xl shadow-sm border border-stone-100 p-6 flex items-center justify-between"
              data-testid={`prayer-${prayer.name.toLowerCase()}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
                  <Icon className="w-7 h-7 text-emerald-700" />
                </div>
                <div>
                  <h3 className="text-lg font-body font-semibold text-text-primary">
                    {prayer.name}
                  </h3>
                  <p className="text-xs font-body text-text-secondary">{prayer.desc}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-body font-bold text-emerald-800 tabular-nums">
                  {prayer.time}
                </div>
                <span className="text-xs text-text-secondary">WIB</span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default JadwalPage;